import { useEffect, useState } from 'react'

/**
 * Reusable Input textarea Component with character counter
 *
 * Features:
 * - Real-time character counting
 * - Visual feedback with color-coded status
 * - Customizable minimum length validation
 * - Backward compatible with existing implementations
 *
 * @param minLength - Minimum required characters (optional)
 * @param showCounter - Whether to display the character counter (optional)
 */
const InputTextArea: React.FC<{
  label: string
  name: string
  value?: string
  handleInputChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  errors?: { [key: string]: string }
  rows?: number
  disabled?: boolean
  minLength?: number
  showCounter?: boolean
  placeholder?: string
}> = ({ label, name, value, handleInputChange, errors, rows, disabled, minLength, showCounter, placeholder }) => {
  const [inputClass, setInputClass] = useState<string>('')
  const inputClassDef = 'border rounded p-2'

  // Character counter logic
  const currentLength = value?.length || 0
  const remaining = minLength ? Math.max(0, minLength - currentLength) : 0

  // Get counter status and styling
  const getCounterStatus = () => {
    if (!minLength) return { text: '', className: '' }

    if (currentLength < minLength) {
      return {
        text: `Faltan ${remaining} caracteres para llegar a una buena descripción`,
        className: 'text-red-600 font-medium',
      }
    } else if (currentLength >= minLength && currentLength < minLength + 15) {
      return {
        text: `Mínimo alcanzado (${currentLength - minLength} sobre el mínimo)`,
        className: 'text-orange-600 font-medium',
      }
    } else {
      return {
        text: `${currentLength - minLength} caracteres sobre el mínimo`,
        className: 'text-green-600 font-medium',
      }
    }
  }

  const counterStatus = getCounterStatus()

  // Si el user escribe algo, borramos el borde rojo
  const handleInputChangeInt = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target
    const hayError = errors && errors[name]

    if (hayError && value !== '') {
      setInputClass(inputClassDef)
    }

    if (handleInputChange) handleInputChange(e)
  }

  useEffect(() => {
    const hayError = errors && errors[name]
    const borderColor = hayError ? 'ring-4 ring-red-500' : ''
    setInputClass(`${inputClassDef} ${borderColor}`)
  }, [errors, name])

  if (!rows) rows = 2

  return (
    <div className="flex flex-col">
      <label className="font-semibold mb-1">{label}</label>
      <textarea
        className={inputClass}
        name={name}
        value={value}
        onChange={handleInputChangeInt}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
      />
      {showCounter && minLength && (
        <div className="mt-1 text-sm">
          <span className={counterStatus.className}>{counterStatus.text}</span>
        </div>
      )}
    </div>
  )
}
export default InputTextArea
